import cn from "classnames";

// One disclaimer, adapted per item, so every AI-produced surface in
// Investigations carries the same message the Statutes panel does: this was
// generated automatically, it can be wrong, and the investigator decides.
export type AiDisclaimerKind =
  | "brief"
  | "callSummary"
  | "callsList"
  | "flags"
  | "leads"
  | "glossary"
  | "entities"
  | "topics"
  | "timeline"
  | "documentSummary"
  | "keyDates"
  | "documentFlags"
  | "transcript"
  | "chat"
  | "policyChat";

const COPY: Record<AiDisclaimerKind, string> = {
  brief:
    "This brief was written automatically from the call transcripts and uploaded files and may contain errors, omissions, or misreadings. It is the responsibility of the reviewing investigator to verify every statement against the source audio and documents and decide for themselves what the evidence establishes before relying on it.",
  callSummary:
    "This summary and relevance score were generated automatically from the transcript and may contain errors or inaccuracies. It is the responsibility of the reviewing investigator to listen to the call and decide for themselves what it establishes before relying on it.",
  callsList:
    "Scores and summaries are generated automatically and may be inaccurate. It is the responsibility of the reviewing investigator to listen to each call and decide for themselves what it establishes before relying on a score.",
  flags:
    "Flagged moments are identified automatically and may be wrong, mistimed, or missing. It is the responsibility of the reviewing investigator to listen to each moment and decide for themselves whether it is significant before acting on it.",
  leads:
    "Leads are identified automatically and may be wrong, mistimed, or missing. It is the responsibility of the reviewing investigator to check each one against the source audio or document and decide for themselves whether it is significant before acting on it.",
  glossary:
    "Slang and coded-language interpretations are automatic readings of context and may be wrong. It is the responsibility of the reviewing investigator to verify each meaning independently before relying on it.",
  entities:
    "People, numbers, and places are extracted automatically and may be misidentified, mislinked, or missing. It is the responsibility of the reviewing investigator to confirm each identity before relying on it.",
  topics:
    "Topics are assigned automatically from the transcript and may be inaccurate. Verify against the call before relying on them.",
  timeline:
    "Timeline entries are placed automatically from transcripts and documents, and dates or attributions may be wrong. It is the responsibility of the reviewing investigator to verify each entry against its source before relying on this chronology.",
  documentSummary:
    "This summary was generated automatically from the document and may contain errors or omissions. It is the responsibility of the reviewing investigator to read the document itself and decide for themselves what it establishes before relying on it.",
  keyDates:
    "Key dates are extracted automatically and may be misread or misattributed. It is the responsibility of the reviewing investigator to verify each date against the document before relying on it.",
  documentFlags:
    "Items flagged in this file are identified automatically and may be wrong or missing. It is the responsibility of the reviewing investigator to check each one against the document and decide for themselves whether it is significant before acting on it.",
  transcript:
    "This transcript, its speaker labels, and any translation were generated automatically and may contain errors. It is the responsibility of the reviewing investigator to verify wording and speakers against the audio before relying on them.",
  chat:
    "Answers are generated automatically from the case materials and may be wrong or incomplete, even when cited. It is the responsibility of the reviewing investigator to verify each answer against the source before relying on it.",
  policyChat:
    "Answers are generated automatically from your department's documents and may be wrong or incomplete, even when cited. Read the cited section before relying on an answer, and follow your chain of command on anything that matters.",
};

type Props = {
  kind: AiDisclaimerKind;
  /** `sm` for rail sections, `md` for page-level surfaces, `inline` for a plain line without the box. */
  size?: "sm" | "md" | "inline";
  className?: string;
};

const AiDisclaimer = ({ kind, size = "md", className }: Props) => {
  if (size === "inline") {
    return (
      <p className={cn("text-[11px] leading-4 text-base-content/50", className)}>
        {COPY[kind]}
      </p>
    );
  }
  return (
    <div
      className={cn(
        "rounded-lg bg-blue-50 dark:bg-blue-900/40",
        size === "sm" ? "p-2" : "p-3",
        className,
      )}
    >
      <p
        className={cn(
          "m-0 p-0 text-base-content/80",
          size === "sm" ? "text-[11px] leading-4" : "text-xs leading-5",
        )}
      >
        {COPY[kind]}
      </p>
    </div>
  );
};

export default AiDisclaimer;
